/*
Grilla Expandible, con un boton que permite agregar datos a la cola.

*/
import React, { useState, useEffect } from 'react'

import { FormatProps, handleInputChangeFormat } from '../utils/handleInputChangeFormat'
import { dateToday, formatDate } from '../utils/utils'
import { Rule, validateFields } from '../utils/validateForm'
import { ExpenseFormat } from '../interfaces/VehicleBase'

import ConfirmationModal from './ConfirmationModal'
import { ButtonActionShort, ButtonDelete } from './Buttons'
import InputField from './InputField'
import CheckboxField from './CheckBoxField'

interface Otro {
  date: string
  price: number
  price_format: string
  description: string
}

interface GridExpProps {
  title: string
  array: ExpenseFormat[]
  onCostsChange: (costs: ExpenseFormat[]) => void
  showExcluded?: boolean
  onChangeExcluded?: (cost: number, excluded: boolean) => void
  canModify?: boolean
  disabled?: boolean
  // Permite ofrecer, solo para gastos nuevos agregados en esta sesión, la opción de
  // guardarlos también en el registro permanente del vehículo (además de la liquidación).
  allowPushToVehicle?: boolean
}

const isNewCost = (id: string | undefined): boolean => !!id && id.startsWith('new_')

const SettlementCost: React.FC<GridExpProps> = ({
  title,
  array,
  onCostsChange,
  showExcluded,
  onChangeExcluded,
  canModify = true,
  disabled = false,
  allowPushToVehicle = false,
}) => {
  const iniOtro: Otro = {
    date: dateToday(),
    price: 0,
    price_format: '',
    description: '',
  }
  const generateCost = (): ExpenseFormat => ({
    _id: `new_${Math.floor(Math.random() * 1000000).toString()}`,
    description: '',
    date: dateToday(),
    cost: 0,
    cost_format: '',
    excluded: false,
    pushToVehicle: false,
  })
  const iniCost = generateCost()
  const [costs, setCosts] = useState<ExpenseFormat[]>(array)
  const [showCostoEdit, setShowCostoEdit] = useState(false)
  const [otro, setOtro] = useState<Otro | null>(iniOtro)
  const [newCost, setNewCost] = useState<ExpenseFormat | null>(iniCost)
  // Errores de validacion de los input
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null)

  useEffect(() => {
    onCostsChange(costs)
  }, [costs])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const formatProps: FormatProps[] = [{ name: 'price', name_format: 'price_format', isDecimal: false }]
    handleInputChangeFormat<Otro>(e, otro, setOtro, formatProps)
  }
  // ACA ESTAN LOS CLICKS EN BOTONES=  ====================================
  const handleAgregarClick = () => {
    if (disabled) return
    setNewCost(iniCost)
    setShowCostoEdit(true)
  }

  // Cuando acepta los valores del cuadro de Adicionales
  const handlePagoAceptarClick = () => {
    let fieldsToValidate = []

    fieldsToValidate = [
      { name: 'price_format', value: otro?.price_format, rule: Rule.GREATER_ZERO },
      { name: 'date', value: otro?.date, rule: Rule.REQUIRED },
      { name: 'description', value: otro?.description, rule: Rule.REQUIRED },
    ]

    const validationErrors = validateFields(fieldsToValidate)

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors) // Set errors if any

      return
    }
    setErrors({})

    if (newCost) {
      let updatedCost = { ...newCost }
      const desc_value = otro?.description ?? ''
      const price = otro?.price ?? 0

      const price_format = otro?.price_format ?? ''
      const date = otro?.date ?? ''

      updatedCost = {
        ...updatedCost,
        description: desc_value,
        cost: price,
        cost_format: price_format,
        date: date,
      }

      // Set the updated payment and push it to payments array
      if (updatedCost) {
        setNewCost({ ...updatedCost })
        // Esto es para resolver un problema de la "asincronicidad "
        setCosts((prevPayments) => [...prevPayments, updatedCost])
        // reseteamos
        clear()
      }
      setShowCostoEdit(false)
    }
  }

  const handlePagoCancelarClick = () => {
    setShowCostoEdit(false)
    clear()
  }
  const onDeleteClick = (key: string) => {
    if (!key) return // Skip if key is empty
    setSelectedItemId(key) // Store the item ID in the state
    setIsModalOpen(true)
  }
  //=========================================================================
  // FIN DE CLICKS EN BOTONES ==========================================

  const handleConfirm = () => {
    setIsModalOpen(false)
    // Perform delete action here
    setCosts((prevItems) => prevItems.filter((item) => item._id !== selectedItemId))
    console.log('Item deleted:' + selectedItemId)
    setSelectedItemId(null)
  }

  const handleCancel = () => {
    setIsModalOpen(false)
    setSelectedItemId(null)
  }

  const clear = (): void => {
    setNewCost({ ...iniCost })

    setOtro(iniOtro)
    setErrors({})
  }
  // Function to handle checkbox click
  const handleCheckClick = (id: string) => {
    if (disabled) return
    setCosts((prevCosts) =>
      prevCosts.map((cost) => {
        if (cost._id === id) {
          const updatedExcluded = !cost.excluded // Toggle the excluded value
          // llamamos la funcion externa si es que está definida
          onChangeExcluded && onChangeExcluded(cost.cost, updatedExcluded)

          return { ...cost, excluded: updatedExcluded } // Update the item in state
        }

        return cost // Return unchanged items
      })
    )
  }
  // Function to handle the "also save on vehicle" checkbox click
  const handlePushToVehicleClick = (id: string) => {
    if (disabled) return
    setCosts((prevCosts) =>
      prevCosts.map((cost) => (cost._id === id ? { ...cost, pushToVehicle: !cost.pushToVehicle } : cost))
    )
  }
  // Toggle para el gasto que se está creando (todavía no está en la lista de costs)
  const handleTogglePushToVehicleNew = () => {
    setNewCost((prev) => (prev ? { ...prev, pushToVehicle: !prev.pushToVehicle } : prev))
  }

  // Nota: las clases de grid deben quedar completas y literales para que Tailwind las detecte.
  const gridNumCols =
    showExcluded && allowPushToVehicle
      ? 'grid grid-cols-8 border-t'
      : showExcluded
        ? 'grid grid-cols-7 border-t'
        : allowPushToVehicle
          ? 'grid grid-cols-7 border-t'
          : 'grid grid-cols-6 border-t'

  return (
    <div>
      <div className="flex items-center">
        <div className="text-2xl w-48">{title}</div>
        {canModify && (
          <div className="flex-1">
            <ButtonActionShort label="Agregar..." handleButtonClick={handleAgregarClick} />
          </div>
        )}
      </div>
      <div className={`${gridNumCols}`}>
        {costs.map((cost) => (
          <React.Fragment key={cost._id || `temp-${Math.random()}`}>
            {showExcluded && (
              <CheckboxField
                key={cost._id || `temp-${Math.random()}`}
                label={''}
                name={'exclude'}
                value={cost._id || ''}
                checked={!cost.excluded}
                handleInputChange={() => handleCheckClick(cost._id || '')}
              />
            )}
            <div className="border-b pr-4 col-span-3">{cost.description}</div>
            <div className="border-b pr-4">{formatDate(cost.date)}</div>
            <div className="border-b pr-4 text-right">${cost.cost_format}</div>
            {allowPushToVehicle && (
              <div className="border-b pr-4" title="Guardar también este gasto en el registro permanente del vehículo">
                {isNewCost(cost._id) ? (
                  <CheckboxField
                    label={'Agregar gasto al auto'}
                    name={'pushToVehicle'}
                    value={cost._id || ''}
                    checked={!!cost.pushToVehicle}
                    handleInputChange={() => handlePushToVehicleClick(cost._id || '')}
                  />
                ) : null}
              </div>
            )}
            {canModify && (
              <ButtonDelete
                key={cost._id || `temp-${Math.random()}`}
                handleButtonClick={() => onDeleteClick(cost._id || '')}
              />
            )}
          </React.Fragment>
        ))}
      </div>
      {isModalOpen && (
        <ConfirmationModal
          message="¿Estás seguro de eliminar este costo adicional?"
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}

      {showCostoEdit && (
        <>
          <div className="border-2 mt-1 pl-1 pr-1 pb-1">
            <div className="flex flex-row gap-4  mt-4">
              <InputField
                label="Descripción"
                name="description"
                value={otro?.description ?? ''}
                type="text"
                handleInputChange={handleInputChange}
                errors={errors}
              />

              <InputField
                label="Fecha"
                name="date"
                value={otro?.date}
                type="date"
                handleInputChange={handleInputChange}
                errors={errors}
              />

              <InputField
                label="Valor"
                name="price_format"
                value={otro?.price_format}
                type="string"
                handleInputChange={handleInputChange}
                errors={errors}
              />
            </div>
            {allowPushToVehicle && (
              <div className="mt-2">
                <CheckboxField
                  label="Agregar gasto al auto"
                  name="pushToVehicleNew"
                  value="pushToVehicleNew"
                  checked={!!newCost?.pushToVehicle}
                  handleInputChange={handleTogglePushToVehicleNew}
                />
              </div>
            )}
          </div>
          <div className="flex justify-center mt-2 space-x-4">
            <ButtonActionShort label="Aceptar" handleButtonClick={handlePagoAceptarClick} />
            <ButtonActionShort label="Cancelar" handleButtonClick={handlePagoCancelarClick} />
          </div>
        </>
      )}
    </div>
  )
}

export default SettlementCost
